import { ActionKeys } from '~/actions/constants';
import { sendGTMEvent } from '~/actions/gtm.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import * as storeActions from '~/actions/stores.actions';
import { CONNECTED_STORE_SUCCESS, FIRST_STORE_CONNECTED } from '~/constants/gtm';
import difference from 'lodash-es/difference';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getConnectedStores } from '~/selectors/stores.selectors';
import * as storeServices from '~/services/stores.service';
import Store, { fromJSON, fromJSONLegacyStore } from '~/types/Store';
import { readFromLocalStorage, saveInLocalStorage } from '~/utils/local-storage';

export function* fetchStores() {
  try {
    const response = yield call(storeServices.fetchStores);
    const stores = response.data.results.map(fromJSON);
    yield put(storeActions.fetchStoresSuccess(stores));
    yield put(storeActions.fetchLegacyStores());
    yield call(checkAndTrackStoreConnectionBehavior);
  } catch (err) {
    yield put(storeActions.fetchStoresError(err.toString()));
  }
}

export function* fetchLegacyStores() {
  try {
    const connectedStores: Store[] = yield select(getConnectedStores);
    const response = yield call(storeServices.fetchLegacyStores);
    // Convert legacy format to Store
    // Filter out already fetched stores
    // These stores were fetched using the new /stores endpoint, which should work
    // for most stores
    const stores = response.data.shops
      .map(fromJSONLegacyStore)
      .filter((store) => store) // Filter null values since this API returns non-connected stores
      .filter(
        (store: Store) =>
          !connectedStores.find(
            (connectedStore: Store) =>
              connectedStore.storePlatform.name === store.storePlatform.name,
          ),
      );

    yield put(storeActions.fetchLegacyStoresSuccess(stores));
  } catch (err) {
    yield put(storeActions.fetchLegacyStoresError(err.toString()));
  }
}

export function* disconnectStore(action: ReturnType<typeof storeActions.disconnectStore>) {
  const storeToDisconnect = action.payload.storeName;
  const storeIdToDisconnect = action.payload.objectId;

  try {
    yield call(storeServices.disconnectStore, storeToDisconnect, storeIdToDisconnect);
    yield put(storeActions.disconnectStoreSuccess(storeToDisconnect));
    yield put(storeActions.fetchStores());
    yield put(storeActions.fetchLegacyStores());
  } catch (e) {
    yield put(storeActions.disconnectStoreError(storeToDisconnect, e.toString()));
  }
}

function* checkAndTrackStoreConnectionBehavior() {
  const connectedStores: Store[] = yield select(getConnectedStores);
  const currentlyConnectedStoreNames = connectedStores.map((x) => x.storePlatform.name);
  const persistedStores = readFromLocalStorage('stores');
  const updateStoresCookie = () =>
    saveInLocalStorage('stores', JSON.stringify(currentlyConnectedStoreNames));
  if (persistedStores) {
    const parsedPersistedStores: readonly string[] = JSON.parse(persistedStores);

    if (parsedPersistedStores.length !== connectedStores.length) {
      const storeName = difference(parsedPersistedStores, currentlyConnectedStoreNames);
      yield put(
        sendTrackEvent('Store Connect Success', {
          Store: storeName,
        }),
      );
      yield put(
        sendGTMEvent({
          eventName: CONNECTED_STORE_SUCCESS,
          data: {
            store: storeName,
          },
        }),
      );
      if (parsedPersistedStores.length === 0 && connectedStores.length === 1) {
        yield put(
          sendGTMEvent({
            eventName: FIRST_STORE_CONNECTED,
            data: {
              store: storeName,
            },
          }),
        );
        yield put(
          sendTrackEvent('First Store Connected', {
            Store: storeName,
          }),
        );
      }
      updateStoresCookie();
    }
  } else {
    updateStoresCookie();
  }
}

export function* editStore(action: ReturnType<typeof storeActions.editStore>) {
  try {
    const response = yield call(storeServices.editStore, action.payload.store);
    const store = fromJSON(response.data);
    yield put(storeActions.editStoreSuccess(store));
  } catch (err) {
    yield put(storeActions.fetchStoresError(err.toString()));
  }
}

export default [
  takeLatest(ActionKeys.STORES_FETCH, fetchStores),
  takeLatest(ActionKeys.LEGACY_STORES_FETCH, fetchLegacyStores),
  takeLatest(ActionKeys.DISCONNECT_STORE, disconnectStore),
  takeLatest(ActionKeys.STORE_EDIT, editStore),
];
